import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { adminAPI } from '../../services/api'
import Card from '../../components/ui/Card'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import StatCard from '../../components/dashboard/StatCard'
import { ExpenseTrendChart, CategoryPieChart } from '../../components/charts'
import toast from 'react-hot-toast'
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ReceiptPercentIcon,
  ShieldCheckIcon,
  NoSymbolIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'

const UserDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation('admin')
  const { t: tc } = useTranslation('common')
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  const [confirm, setConfirm] = useState(null)

  const { data, isLoading, error } = useQuery(
    ['admin-user-detail', id],
    () => adminAPI.getUserDetail(id),
    { enabled: !!id }
  )

  const updateMutation = useMutation(
    (body) => adminAPI.updateUser(id, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-user-detail', id])
        queryClient.invalidateQueries(['admin-users'])
        toast.success(t('users.updated'))
        setConfirm(null)
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || t('users.updateError'))
        setConfirm(null)
      },
    }
  )

  const handleConfirm = () => {
    if (!confirm) return
    if (confirm.type === 'role') {
      updateMutation.mutate({ role: user.role === 'admin' ? 'user' : 'admin' })
    } else if (confirm.type === 'status') {
      updateMutation.mutate({ is_active: !user.is_active })
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-20">
        <p className="text-lg font-semibold text-[var(--color-text)]">
          {t('detail.userNotFound')}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/users')}>
          {t('detail.backToList')}
        </Button>
      </div>
    )
  }

  const { user, stats, monthly_trend, top_categories, recent_documents, active_budgets } = data.data
  const isSelf = user.id === currentUser?.id

  const getInitials = () => {
    if (user.first_name && user.last_name) {
      return (user.first_name[0] + user.last_name[0]).toUpperCase()
    }
    return (user.username || user.email)[0].toUpperCase()
  }

  const getName = () => {
    if (user.first_name || user.last_name) {
      return [user.first_name, user.last_name].filter(Boolean).join(' ')
    }
    return user.username
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return t('users.never')
    return new Date(dateStr).toLocaleDateString('pl-PL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }

  const formatCurrency = (val) =>
    parseFloat(val || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' zł'

  return (
    <div className="space-y-6">
      {/* Header z profilem */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center gap-1.5 text-sm font-medium transition-colors self-start"
          style={{ color: '#1f9f93' }}
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t('detail.backToList')}
        </button>
      </div>

      <Card>
        <Card.Body>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
                style={{ background: isSelf ? '#1f9f93' : 'var(--color-text-faint)' }}
              >
                {getInitials()}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-bold text-[var(--color-text)]">
                    {getName()}
                  </h2>
                  <Badge variant={user.role === 'admin' ? 'primary' : 'secondary'} size="sm">
                    {user.role === 'admin' ? t('users.roleAdmin') : t('users.roleUser')}
                  </Badge>
                  <Badge variant={user.is_active ? 'success' : 'error'} size="sm">
                    {user.is_active ? t('users.active') : t('users.inactive')}
                  </Badge>
                  {isSelf && (
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: 'rgba(31,159,147,0.12)', color: '#1f9f93' }}>
                      Ty
                    </span>
                  )}
                </div>
                <p className="text-sm mt-0.5 text-[var(--color-text-muted)]">
                  {user.email}
                </p>
                <div className="flex gap-4 mt-1 text-xs text-[var(--color-text-faint)]">
                  <span>{t('detail.memberSince')}: {formatDate(user.createdAt)}</span>
                  <span>{t('users.lastLogin')}: {formatDate(user.last_login)}</span>
                </div>
              </div>
            </div>

            {/* Akcje */}
            {!isSelf && (
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirm({ type: 'role' })}
                >
                  <ShieldCheckIcon className="h-4 w-4 mr-1" />
                  {user.role === 'admin' ? t('users.makeUser') : t('users.makeAdmin')}
                </Button>
                <Button
                  variant={user.is_active ? 'outline' : 'primary'}
                  size="sm"
                  onClick={() => setConfirm({ type: 'status' })}
                >
                  {user.is_active ? (
                    <><NoSymbolIcon className="h-4 w-4 mr-1" />{t('users.deactivate')}</>
                  ) : (
                    <><CheckCircleIcon className="h-4 w-4 mr-1" />{t('users.activate')}</>
                  )}
                </Button>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('detail.documents')}
          value={stats.documents}
          icon={DocumentTextIcon}
          color="primary"
          delay={0}
        />
        <StatCard
          title={t('detail.expenses')}
          value={formatCurrency(stats.expenses_total)}
          change={stats.expenses > 0 ? `${stats.expenses} ${t('detail.transactions')}` : null}
          icon={BanknotesIcon}
          color="warning"
          delay={0.05}
        />
        <StatCard
          title={t('detail.incomes')}
          value={formatCurrency(stats.incomes_total)}
          change={stats.incomes > 0 ? `${stats.incomes} ${t('detail.transactions')}` : null}
          icon={ArrowTrendingUpIcon}
          color="success"
          delay={0.1}
        />
        <StatCard
          title={t('detail.receipts')}
          value={stats.receipts}
          change={stats.expenses_avg > 0 ? `${t('detail.avgExpense')}: ${formatCurrency(stats.expenses_avg)}` : null}
          icon={ReceiptPercentIcon}
          color="secondary"
          delay={0.15}
        />
      </div>

      {/* Wykresy */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <Card.Header>
            <h3 className="text-sm font-semibold text-[var(--color-text)]">
              {t('detail.expenseTrend')}
            </h3>
          </Card.Header>
          <Card.Body>
            <ExpenseTrendChart data={monthly_trend} height={260} />
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <h3 className="text-sm font-semibold text-[var(--color-text)]">
              {t('detail.topCategories')}
            </h3>
          </Card.Header>
          <Card.Body>
            <CategoryPieChart data={top_categories} height={260} />
          </Card.Body>
        </Card>
      </div>

      {/* Ostatnie dokumenty */}
      <Card>
        <Card.Header>
          <h3 className="text-sm font-semibold text-[var(--color-text)]">
            {t('detail.recentDocuments')}
          </h3>
        </Card.Header>
        <Card.Body style={{ padding: 0 }}>
          {(!recent_documents || recent_documents.length === 0) ? (
            <div className="text-center py-8 text-sm text-[var(--color-text-faint)]">
              {t('detail.noDocuments')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-faint)]">
                      {t('detail.docName')}
                    </th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-faint)]">
                      {t('detail.docType')}
                    </th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-faint)]">
                      {t('detail.docStatus')}
                    </th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-faint)]">
                      {t('detail.docDate')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recent_documents.map((doc) => (
                    <tr
                      key={doc.id}
                      className="transition-colors hover:bg-[var(--color-hover)] cursor-pointer border-b border-[var(--color-border)]"
                      onClick={() => navigate(`/documents/${doc.id}`)}
                    >
                      <td className="px-4 py-2.5 text-sm font-medium text-[var(--color-text)]">
                        {doc.original_name || doc.title || doc.id}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-[var(--color-text-muted)]">
                        {doc.document_type || '-'}
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge
                          variant={doc.status === 'processed' ? 'success' : doc.status === 'error' ? 'error' : 'warning'}
                          size="xs"
                        >
                          {doc.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-[var(--color-text-muted)]">
                        {formatDate(doc.created_at || doc.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Aktywne budżety */}
      <Card>
        <Card.Header>
          <h3 className="text-sm font-semibold text-[var(--color-text)]">
            {t('detail.activeBudgets')}
          </h3>
        </Card.Header>
        <Card.Body>
          {(!active_budgets || active_budgets.length === 0) ? (
            <div className="text-center py-8 text-sm text-[var(--color-text-faint)]">
              {t('detail.noBudgets')}
            </div>
          ) : (
            <div className="space-y-3">
              {active_budgets.map((budget) => {
                const pct = Math.min(budget.spent_percentage, 100)
                const isOver = budget.spent_percentage > 100
                return (
                  <div key={budget.id} className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-[var(--color-text)]">
                        {budget.category?.name || budget.name || 'Budget'}
                        {budget.period && (
                          <span className="ml-2 text-xs text-[var(--color-text-faint)]">
                            ({budget.period})
                          </span>
                        )}
                      </div>
                      <div className={`text-xs ${isOver ? 'text-[#ef4444]' : 'text-[var(--color-text-muted)]'}`}>
                        {t('detail.spent')} {formatCurrency(budget.spent)} {t('detail.of')} {formatCurrency(budget.amount)}
                      </div>
                    </div>
                    <div className="w-full h-2 rounded-full bg-[var(--color-skeleton)]">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{
                          width: `${pct}%`,
                          background: isOver ? '#ef4444' : budget.spent_percentage > 80 ? '#f59e0b' : '#1f9f93',
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Confirmation dialog */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-[var(--color-surface)] rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold mb-3 text-[var(--color-text)]">
              {confirm.type === 'status'
                ? (user.is_active ? t('users.deactivate') : t('users.activate'))
                : (user.role === 'admin' ? t('users.makeUser') : t('users.makeAdmin'))}
            </h3>
            <p className="text-sm mb-6 text-[var(--color-text-muted)]">
              {confirm.type === 'status'
                ? t('users.confirmDeactivate', { name: getName() })
                : t('users.confirmRoleChange', {
                    name: getName(),
                    role: user.role === 'admin' ? t('users.roleUser') : t('users.roleAdmin'),
                  })}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" size="sm" onClick={() => setConfirm(null)}>
                {tc('cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={updateMutation.isLoading}
                onClick={handleConfirm}
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserDetailPage
